import type { IconProps as PhosphorIconProps } from "@phosphor-icons/react";
// Import SSR-compatible icons (use /ssr, not /dist/ssr)
import * as PhosphorIconsSSR from "@phosphor-icons/react/ssr";
import React, { memo } from "react";
import { twMerge } from "tailwind-merge";

// Type for any Phosphor icon component
type PhosphorIconComponent = React.ComponentType<PhosphorIconProps>;

// Extract all icon names from the Phosphor package
export type PhosphorIconName = string;

// For backwards compatibility, export IconName as an alias
export type IconName = PhosphorIconName;

// Standard size presets for the design system
const sizePresets = {
  xs: 16,
  sm: 20,
  md: 24,
  lg: 32,
  xl: 40,
  "2xl": 48,
} as const;

type SizePreset = keyof typeof sizePresets;

interface IconSSRProps extends Omit<PhosphorIconProps, "size"> {
  /**
   * The name of the Phosphor icon to render
   * Any valid icon from @phosphor-icons/react
   */
  name: PhosphorIconName;

  /**
   * Size of the icon - can be a preset or custom number
   */
  size?: SizePreset | number;

  /**
   * Accessibility label (maps to aria-label)
   */
  ariaLabel?: string;
}

/**
 * IconSSR
 *
 * SSR-compatible Phosphor icon wrapper component with standardized sizing and styling.
 * Uses the SSR-compatible version of phosphor-icons for server-side rendering.
 * - TypeScript autocomplete for all icon names
 * - SSR-compatible: Uses SSR-optimized icons (no Context API dependency)
 * - Tree-shaking: Only icons you use are included in the bundle
 *
 * Usage:
 * ```tsx
 * <IconSSR name="House" size="md" />
 * <IconSSR name="User" size={32} className="text-brand" />
 * ```
 */
export const IconSSR = memo(
  ({ name, size = "md", color, weight = "regular", className, ariaLabel, ...props }: IconSSRProps) => {
    // Get the icon component from the SSR Phosphor package
    const IconComponent = (PhosphorIconsSSR as unknown as Record<string, PhosphorIconComponent>)[name];

    if (!IconComponent) {
      // Icon not found - return null silently in production
      return null;
    }

    // Resolve size to pixel value
    const resolvedSize: number =
      typeof size === "string"
        ? size in sizePresets
          ? sizePresets[size as SizePreset]
          : sizePresets.md // Fallback to md if invalid preset
        : size;

    // Build className with design system defaults
    const iconClassName = twMerge(
      // Default styles
      "inline-block flex-shrink-0",
      // Allow color inheritance from parent
      !color && "text-current",
      // Custom classes
      className
    );

    return (
      <IconComponent
        {...props}
        size={resolvedSize}
        color={color}
        weight={weight}
        className={iconClassName}
        aria-label={ariaLabel || `${name} icon`}
        data-testid={`icon-${name}`}
      />
    );
  }
);

IconSSR.displayName = "IconSSR";

/**
 * Helper component for icons with containers (SSR version)
 */
interface IconSSRWithContainerProps extends IconSSRProps {
  /**
   * Container variant
   */
  variant?: "subtle" | "solid" | "outline";

  /**
   * Container shape
   */
  shape?: "square" | "circle";

  /**
   * Container size (adds padding around icon)
   */
  containerSize?: SizePreset | number;

  /**
   * Container background color
   */
  containerClassName?: string;
}

export const IconSSRWithContainer = memo(
  ({
    variant = "subtle",
    shape = "square",
    containerSize,
    containerClassName,
    size = "md",
    className,
    name,
    color,
    weight,
    ariaLabel,
    ref,
    ...props
  }: IconSSRWithContainerProps & { ref?: React.Ref<HTMLDivElement> }) => {
    // Calculate container size if not specified
    const iconSize: number =
      typeof size === "string" ? (size in sizePresets ? sizePresets[size as SizePreset] : sizePresets.md) : size;

    const resolvedContainerSize: number = containerSize
      ? typeof containerSize === "string"
        ? containerSize in sizePresets
          ? sizePresets[containerSize as SizePreset]
          : iconSize * 1.75 // Fallback to default if invalid preset
        : containerSize
      : iconSize * 1.75;

    const containerClasses = twMerge(
      // Base styles
      "inline-flex items-center justify-center flex-shrink-0",

      // Shape
      shape === "circle" ? "rounded-full" : "rounded-lg",

      // Variant styles using Tailwind 4 CSS variables
      variant === "subtle" && "bg-gray-100 dark:bg-gray-800",
      variant === "solid" && "bg-brand text-white",
      variant === "outline" && "border-2 border-current",

      // Container className for overrides
      containerClassName
    );

    return (
      <div
        className={containerClasses}
        style={{
          width: resolvedContainerSize,
          height: resolvedContainerSize,
        }}
      >
        <IconSSR
          {...props}
          name={name}
          size={size}
          color={color}
          weight={weight}
          className={className}
          ariaLabel={ariaLabel}
        />
      </div>
    );
  }
);

IconSSRWithContainer.displayName = "IconSSRWithContainer";
